import { Table, TableBody, TableCell, TableHeader, TableRow } from "../ui/table";
import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { ChevronDownIcon, CloseIcon, ChevronLeftIcon } from "../../icons";
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { PrinterIcon } from "@heroicons/react/24/outline";
import {
  DiningRequest as OriginalDiningRequest,
  RequestStatus,
} from "../../types/request";
import RequestsTab from "../common/RequestsTab";
import Alert from "../ui/alert/Alert";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { PageLoadingView } from "../common/PageLoadingView";
import { PageErrorView } from "../common/PageErrorView";
import React from "react";
import DOMPurify from "dompurify";
import SimpleDate from "../common/SimpleDate";
import { useDiningRequests } from "../../hooks/useDiningRequests";
import { useMessageTypes } from "../../hooks/useMessageTypes";
import { useUser } from "../../context/UserInfoProvider";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useHotelInfo } from "../../hooks/useHotelInfo";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useRequestRefresh } from "../../hooks/useRequestRefresh";
import ConfirmationDialog from "../common/ConfirmationDialog";
import MessageHistorySidebar from "../common/MessageHistorySidebar";
import { RequestsToolbar } from "../common/RequestsToolbar";

// OriginalDiningRequest from types/request.ts now includes platformUser.
// This local interface extension is only for roomServiceMenuId.
interface DiningRequest extends OriginalDiningRequest {
  roomServiceMenuId?: string;
  guestName: string;
}

// EmployeeSelfResponse interface is no longer needed.

// Helper to display nullable fields
const displayValue = (
  value: string | number | null | undefined,
  defaultValue: string = "N/A",
) => {
  return value !== null && value !== undefined && String(value).trim() !== ""
    ? String(value)
    : defaultValue;
};

// Helper to get error message safely
const getErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  return String(error);
};

// Helper to format date with timezone support
const formatDateWithTimezone = (
  dateString: string,
  timezone?: string | null,
) => {
  const date = new Date(dateString);

  if (timezone) {
    // Use the hotel's timezone
    return date.toLocaleString("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    // Fallback to local timezone
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

export default function DiningReqs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { callApi } = useAuthenticatedApi();
  const { platformUserId } = useUser();
  const analytics = useAnalytics();
  const { data: hotelInfo } = useHotelInfo();
  const [selectedSort, setSelectedSort] = useState("Time");
  const [selectedFrame, setSelectedFrame] = useState("Active");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedRequest, setSelectedRequest] = useState<DiningRequest | null>(
    null,
  );
  const [starredList, setStarredList] = useState<Set<string>>(new Set());
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isPushingStatus, setIsPushingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState("");
  const [showPushAlert, setShowPushAlert] = useState(false);
  const [showStatusUpdateAlert, setShowStatusUpdateAlert] = useState(false);
  const [isSidebarStatusDropdownOpen, setIsSidebarStatusDropdownOpen] =
    useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);

  // Track which status dropdowns are open
  const [statusDropdowns, setStatusDropdowns] = useState<{
    [key: string]: boolean;
  }>({});

  // Track which items are being updated
  const [updatingStatus, setUpdatingStatus] = useState<{
    [key: string]: boolean;
  }>({});

  // Refs for status dropdown buttons
  const statusButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  // Local search term state for UI
  const [searchTermLocal, setSearchTermLocal] = useState<string>("");

  // Determine status filter based on selected frame and status filter
  const statusFilter = useMemo(() => {
    // If a specific status is selected (not "All"), use only that status
    if (selectedStatus !== "All") {
      const statusMap: Record<string, string> = {
        Submitted: "Submitted",
        "In Progress": "InProgress",
        Delayed: "Delayed",
        Scheduled: "Scheduled",
        Completed: "Completed",
        Cancelled: "Cancelled",
      };
      const apiStatus = statusMap[selectedStatus];
      return apiStatus ? [apiStatus as RequestStatus] : undefined;
    }

    // Otherwise, use frame-based filtering
    if (selectedFrame === "Active") {
      return [
        "Submitted",
        "InProgress",
        "Delayed",
      ] as unknown as RequestStatus[];
    } else if (selectedFrame === "Scheduled") {
      return ["Scheduled"] as unknown as RequestStatus[];
    } else if (selectedFrame === "History") {
      return ["Completed", "Cancelled"] as unknown as RequestStatus[];
    }
    return undefined;
  }, [selectedFrame, selectedStatus]);

  // Use the dining requests hook
  const {
    requests: fetchedRequests,
    isLoading,
    error,
    updateRequestStatusAsync,
    fetchRequestById,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  } = useDiningRequests({
    hotelId: hotelInfo?.hotelid || "",
    initialPage: 1,
    initialLimit: 10,
    status: statusFilter,
    sortBy: selectedSort === "Time" ? "createdAt" : "requestId",
    sortOrder: "desc",
  });

  // Setup auto-refresh when navigating to the page and when new requests come in
  useRequestRefresh({
    onRefresh: () => {
      refetch();
    },
    requestType: "dining",
  });

  const [subject, setSubject] = useState("");
  const [menuItemPrices, setMenuItemPrices] = useState<Record<string, number>>(
    {},
  );
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);

  // Message types state
  const { messageTypes, isLoadingMessageTypes, messageTypeError } =
    useMessageTypes();
  const [selectedMessageTypeId, setSelectedMessageTypeId] =
    useState<string>("");

  // Message confirmation and history states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<{
    subject: string;
    content: string;
    typeId: string;
  } | null>(null);
  const [showMessageHistory, setShowMessageHistory] = useState(false);

  // Track navigation state
  const [pendingNavigation, setPendingNavigation] = useState<
    "previous" | "next" | null
  >(null);

  // Auto-select from URL deep link if requestId is present
  const [didInitDeepLink, setDidInitDeepLink] = useState(false);

  // Initialize selected tab from statusTab param
  useEffect(() => {
    const statusTab = new URLSearchParams(window.location.search).get(
      "statusTab",
    );
    if (statusTab && ["Active", "Scheduled", "History"].includes(statusTab)) {
      setSelectedFrame(statusTab);
    }
  }, []);

  // Persist favorites locally (scoped by user and page)
  const favoritesStorageKey = useMemo(() => {
    const uid = platformUserId || "anon";
    return `selfserve:favorites:dining:${uid}`;
  }, [platformUserId]);

  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);

  useEffect(() => {
    setHasLoadedFavorites(false);
  }, [favoritesStorageKey]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(favoritesStorageKey);
      if (raw) {
        const ids: string[] = JSON.parse(raw);
        if (Array.isArray(ids)) setStarredList(new Set(ids));
      } else {
        setStarredList(new Set());
      }
    } catch {
      setStarredList(new Set());
    }
    setHasLoadedFavorites(true);
  }, [favoritesStorageKey]);

  useEffect(() => {
    if (!hasLoadedFavorites) return;
    try {
      localStorage.setItem(
        favoritesStorageKey,
        JSON.stringify(Array.from(starredList)),
      );
    } catch {
      // no-op
    }
  }, [starredList, favoritesStorageKey, hasLoadedFavorites]);

  // Replace the old hook with our authenticated API implementation

  // const normalizeStatus = (status: string): string => { // Unused function removed
  //   return status.toUpperCase().replace(/[\s_]/g, "");
  // };

  // Remove frontend filtering - backend handles search and status filtering
  const filteredData = useMemo(() => {
    return fetchedRequests.filter((request) => {
      const passesStarredFilter = showStarredOnly
        ? starredList.has(request.requestId)
        : true;

      return passesStarredFilter;
    });
  }, [fetchedRequests, showStarredOnly, starredList]);

  const sortedData = useMemo(() => {
    // Sorting handled by backend; keep current page order
    return filteredData;
  }, [filteredData]);

  // No client-side pagination needed - backend handles pagination
  const paginatedData = sortedData;

  useEffect(() => {
    if (didInitDeepLink) return;
    const reqId = searchParams.get("requestId");
    if (!reqId) return;

    // If already selected, mark as done
    if (selectedRequest && selectedRequest.requestId === reqId) {
      setDidInitDeepLink(true);
      return;
    }

    // Check if the request exists in the current page's data
    const match =
      sortedData.find((r) => r.requestId === reqId) ||
      paginatedData.find((r) => r.requestId === reqId);

    if (match) {
      // Request found in current page - select it
      handleSelectRequest(match);
      setDidInitDeepLink(true);
    } else if (!isLoading) {
      // Request not in current page - fetch it directly by ID
      const fetchAndSelectRequest = async () => {
        try {
          const completeRequest = await fetchRequestById(reqId);
          setSelectedRequest(completeRequest);
          setDidInitDeepLink(true);
        } catch (error) {
          console.error("Failed to fetch deep-linked request:", error);
          setDidInitDeepLink(true); // Mark as done even on error to prevent infinite loop
        }
      };
      fetchAndSelectRequest();
    }
  }, [
    searchParams,
    selectedRequest,
    sortedData,
    paginatedData,
    didInitDeepLink,
    isLoading,
    fetchRequestById,
  ]);

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const hasMore = pagination.page < totalPages;

  // Reset pagination when switching tabs or filters
  useEffect(() => {
    setPage(1);
  }, [selectedFrame, selectedStatus, setPage]);

  // Auto-select first message type when available
  useEffect(() => {
    if (messageTypes.length > 0 && !selectedMessageTypeId) {
      setSelectedMessageTypeId(messageTypes[0].typeid);
    }
  }, [messageTypes, selectedMessageTypeId]);

  // Handle navigation across pages - clear sidebar when navigating to new page
  useEffect(() => {
    if (pendingNavigation) {
      setSelectedRequest(null);
    }
  }, [pendingNavigation]);

  // Handle navigation across pages - select appropriate item after page loads
  useEffect(() => {
    if (pendingNavigation && sortedData.length > 0) {
      // Small delay to ensure data is loaded
      const timer = setTimeout(() => {
        if (pendingNavigation === "previous") {
          // Select the last item of the new page
          const lastItem = sortedData[sortedData.length - 1];
          if (lastItem) {
            handleSelectRequest(lastItem);
          }
        } else if (pendingNavigation === "next") {
          // Select the first item of the new page
          const firstItem = sortedData[0];
          if (firstItem) {
            handleSelectRequest(firstItem);
          }
        }
        setPendingNavigation(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingNavigation, sortedData]);

  const fetchMenuItemPrices = async (
    orderItems: DiningRequest["orderItems"],
  ) => {
    if (!orderItems) return;
    setIsLoadingPrices(true);
    try {
      const pricePromises = orderItems.map(async (item) => {
        if (item.priceId) {
          try {
            // TODO: Fix this endpoint - /management/menu-items doesn't exist in backend API
            // Need to fetch price through proper restaurant/menu/item hierarchy
            // const priceResponse = await callApi({
            //   method: 'GET',
            //   url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/items/${itemId}`,
            // });
            const priceResponse = { data: { amount: 0 } }; // Fallback for now
            const priceData = priceResponse.data;
            return { priceId: item.priceId, amount: priceData.amount };
          } catch (error) {
            console.error(
              `Error fetching price for item (ID: ${item.priceId}):`,
              error,
            );
            return null;
          }
        } else {
          console.warn(
            `Skipping price fetch for item due to missing priceId: ${item.itemName}`,
            item,
          );
          return null;
        }
      });
      const priceResults = await Promise.all(pricePromises);
      const validResults = priceResults.filter(
        (result): result is { priceId: string; amount: number } =>
          result !== null &&
          typeof result.priceId === "string" &&
          typeof result.amount === "number",
      );
      const newPrices = Object.fromEntries(
        validResults.map((result) => [result.priceId, result.amount]),
      );
      setMenuItemPrices((prev) => ({ ...prev, ...newPrices }));
    } catch (error) {
      console.error("Error fetching menu item prices:", error);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleSelectRequest = async (req: DiningRequest) => {
    try {
      const completeRequest = await fetchRequestById(req.requestId);
      setSelectedRequest(completeRequest);
      setLiveStatus("");
      setShowPushAlert(false);
      setStatusError(null);
      if (completeRequest.orderItems) {
        await fetchMenuItemPrices(completeRequest.orderItems);
      }
      // Sync URL for deep linking (do not auto-open message history)
      try {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("requestId", req.requestId);
          return next;
        });
      } catch (_e) {
        if (import.meta.env.DEV)
          console.warn("Failed to sync requestId param (1st)", _e);
      }
      try {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("requestId", req.requestId);
          return next;
        });
      } catch (_e) {
        if (import.meta.env.DEV)
          console.warn("Failed to sync requestId param (2nd)", _e);
      }
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to load request details");
    }
  };

  const handleSidebarStatusChange = async (
    newStatusFromDropdown: RequestStatus,
  ) => {
    if (!selectedRequest || isUpdatingStatus) return;
    setIsUpdatingStatus(true);
    setStatusError(null);
    setIsSidebarStatusDropdownOpen(false);
    try {
      let apiCompatibleStatus: string = newStatusFromDropdown;
      if (newStatusFromDropdown === "IN_PROGRESS")
        apiCompatibleStatus = "InProgress";
      else if (newStatusFromDropdown === "SUBMITTED")
        apiCompatibleStatus = "Submitted";
      else if (newStatusFromDropdown === "DELAYED")
        apiCompatibleStatus = "Delayed";
      else if (newStatusFromDropdown === "SCHEDULED")
        apiCompatibleStatus = "Scheduled";
      else if (newStatusFromDropdown === "COMPLETED")
        apiCompatibleStatus = "Completed";
      else if (newStatusFromDropdown === "CANCELLED")
        apiCompatibleStatus = "Cancelled";

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId: selectedRequest.requestId,
        status: apiCompatibleStatus as RequestStatus,
      };
      if (apiCompatibleStatus === "Completed")
        updatePayload.completedAt = new Date().toISOString();

      await updateRequestStatusAsync(updatePayload);
      setSelectedRequest((prev) =>
        prev
          ? {
              ...prev,
              status: apiCompatibleStatus as RequestStatus,
              completedAt:
                apiCompatibleStatus === "Completed"
                  ? updatePayload.completedAt
                  : prev.completedAt,
            }
          : null,
      );

      // Track dining request status change analytics
      analytics.request.statusChange(
        "dining",
        apiCompatibleStatus.toLowerCase(),
        selectedRequest.status?.toLowerCase(),
      );

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from sidebar
      // User should remain on their current tab
    } catch (err) {
      // Friendly error messaging (e.g., missing totalAmount)
      const anyErr = err as any;
      const resp = anyErr?.response?.data;
      const validationType =
        resp?.error?.type || resp?.debugInfo?.errorType || resp?.type;
      if (validationType === "ValidationError") {
        const details = Array.isArray(resp?.details) ? resp.details : [];
        const fields = details
          .map((d: any) => d?.field)
          .filter(Boolean)
          .join(", ");
        const fieldHint =
          fields?.length > 0 ? ` (missing/invalid: ${fields})` : "";
        setStatusError(
          `We couldn't complete this order because required information is missing${fieldHint}. Please contact your administrator to correct the order data.`,
        );
      } else {
        const fallbackMsg =
          anyErr?.message ||
          resp?.error?.message ||
          "Failed to update status. Please try again.";
        setStatusError(fallbackMsg);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handlePushToGuest = async () => {
    if (!liveStatus.trim()) {
      setStatusError("Message content is required");
      return;
    }
    if (!selectedMessageTypeId) {
      setStatusError("Please select a message type.");
      return;
    }
    if (!selectedRequest) {
      setStatusError("No request selected");
      return;
    }
    if (!platformUserId) {
      setStatusError(
        "Sender User ID (platformUserId) is missing or not configured.",
      );
      return;
    }
    if (!selectedRequest.platformUserId) {
      setStatusError(
        "Receiver (guest) platform user ID (platformUserId) is missing for this request.",
      );
      console.error(
        "Receiver platformUserId missing on selectedRequest:",
        selectedRequest,
      );
      return;
    }

    // Show confirmation dialog instead of sending immediately
    setPendingMessage({
      subject: subject || "",
      content: liveStatus,
      typeId: selectedMessageTypeId,
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    if (!pendingMessage || !selectedRequest) return;

    setIsPushingStatus(true);
    setStatusError(null);
    setShowConfirmDialog(false);

    try {
      await callApi({
        method: "POST",
        url: "/management/messages",
        data: {
          senderPlatformUserId: platformUserId,
          receiverPlatformUserId: selectedRequest.platformUserId,
          typeId: pendingMessage.typeId,
          subject: pendingMessage.subject,
          content: pendingMessage.content,
          requestId: selectedRequest.requestId,
        },
      });
      setLiveStatus("");
      setSubject("");
      setPendingMessage(null);
      setShowPushAlert(true);
      setTimeout(() => setShowPushAlert(false), 3000);
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to send message to guest");
      console.error("Error sending message:", error);
    } finally {
      setIsPushingStatus(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmDialog(false);
    setPendingMessage(null);
  };

  const toggleStars = (requestId: string) => {
    setStarredList((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(requestId)) updatedSet.delete(requestId);
      else updatedSet.add(requestId);
      return updatedSet;
    });
  };

  const toggleFilterDropdown = () =>
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  const toggleSortDropdown = () => setIsSortDropdownOpen(!isSortDropdownOpen);

  const toggleStatusDropdown = (requestId: string) => {
    setStatusDropdowns((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key !== requestId) {
          newState[key] = false;
        }
      });
      newState[requestId] = !newState[requestId];
      return newState;
    });
  };

  const handleTableStatusChange = async (
    requestId: string,
    newStatus: string,
  ) => {
    try {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: true }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));

      let apiCompatibleStatus: string = newStatus;
      if (newStatus === "In Progress") apiCompatibleStatus = "InProgress";
      else if (newStatus === "Submitted") apiCompatibleStatus = "Submitted";
      else if (newStatus === "Delayed") apiCompatibleStatus = "Delayed";
      else if (newStatus === "Scheduled") apiCompatibleStatus = "Scheduled";
      else if (newStatus === "Completed") apiCompatibleStatus = "Completed";
      else if (newStatus === "Cancelled") apiCompatibleStatus = "Cancelled";

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId,
        status: apiCompatibleStatus as RequestStatus,
      };

      if (apiCompatibleStatus === "Completed") {
        updatePayload.completedAt = new Date().toISOString();
      }

      await updateRequestStatusAsync(updatePayload);

      // Track dining request status change analytics
      analytics.request.statusChange(
        "dining",
        apiCompatibleStatus.toLowerCase(),
        "",
      );

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from table
      // User should remain on their current tab
    } catch (err) {
      // Provide a user-friendly message, especially for backend validation errors
      const anyErr = err as any;
      const resp = anyErr?.response?.data;
      const validationType =
        resp?.error?.type || resp?.debugInfo?.errorType || resp?.type;
      if (validationType === "ValidationError") {
        const details = Array.isArray(resp?.details) ? resp.details : [];
        const fields = details
          .map((d: any) => d?.field)
          .filter(Boolean)
          .join(", ");
        const fieldHint =
          fields?.length > 0 ? ` (missing/invalid: ${fields})` : "";
        setStatusError(
          `We couldn't complete this order because required information is missing${fieldHint}. Please contact your administrator to correct the order data.`,
        );
      } else {
        const fallbackMsg =
          anyErr?.message ||
          resp?.error?.message ||
          "Failed to update status. Please try again.";
        setStatusError(fallbackMsg);
      }
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: false }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const getFrameClass = () => "text-[#1100D8] font-[300] dark:text-gray-400";

  const getRowClassName = (status: string) => {
    if (status === "COMPLETED") return "bg-gray-50 dark:bg-transparent";
    return "";
  };

  const getStatusDisplay = (status: RequestStatus) => {
    if ((status as string) === "InProgress") return "In Progress";
    return status
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusOptions = () => [
    "Submitted",
    "In Progress",
    "Delayed",
    "Scheduled",
    "Completed",
    "Cancelled",
  ];

  const getFilterOptions = (frame: string) => {
    switch (frame) {
      case "Active":
        return ["All", "Submitted", "In Progress", "Delayed"];
      case "History":
        return ["All", "Completed", "Cancelled"];
      default:
        return [];
    }
  };

  // HTML escaping function to prevent XSS
  const escapeHtml = (text: string | null | undefined): string => {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#x27;");
  };

  // No UI state, but helper function to print
  const generateReceiptHTML = (req: DiningRequest) => {
    const formatDate = (d: string | Date) => new Date(d).toLocaleString();

    const itemsHtml = (req.orderItems || [])
      .map((item) => {
        const unit = parseFloat(item.price?.amount ?? "0");
        const lineTotal = unit * (item.quantity || 1);
        return `
          <div class="item-row" style="margin-bottom:8px;">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;font-weight:bold;">
              <div style="text-align:left;flex:1;min-width:0;line-height:1.15;">
                <div style="margin:0;padding:0;line-height:1.15;">${item.quantity} × ${escapeHtml(
                  item.itemName,
                )}</div>
                ${(() => {
                  const noteText =
                    item.specialInstructions &&
                    item.specialInstructions.trim() !== ""
                      ? item.specialInstructions.trim()
                      : "";
                  return noteText
                    ? `<div style="font-style:italic;font-size:11px;font-weight:normal;color:#555;margin:0;padding:0;line-height:1.15;white-space:pre-wrap;word-break:break-word;text-align:left;">Note: ${escapeHtml(noteText)}</div>`
                    : "";
                })()}
              </div>
              <span>$${lineTotal.toFixed(2)}</span>
            </div>
          </div>
        `;
      })
      .join("");

    return `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Dining Order ${escapeHtml(req.requestId)}</title>
        <style>
          body { 
            font-family: 'Courier New', monospace; 
            width: 80mm; 
            margin: 0 auto; 
            padding: 20px; 
            background: white;
          }
          h2 { 
            text-align: center; 
            margin: 0 0 4px 0; 
            font-size: 18px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          h3 { 
            text-align: center; 
            margin: 0 0 16px 0; 
            font-size: 14px;
            font-weight: normal;
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
          }
          .info-section {
            margin-bottom: 0;
            padding-bottom: 0;
          }
          .row { 
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            font-size: 12px;
          }
          .row strong {
            font-weight: bold;
          }
          .items-section {
            margin: 0;
            padding: 12px 0 0 0;
          }
          .items-header {
            font-weight: bold;
            margin-bottom: 12px;
            font-size: 13px;
            text-transform: uppercase;
          }
          .item-row {
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px dotted #ccc;
          }
          .item-row:last-child {
            border-bottom: none;
          }
          .delivery-section {
            margin: 12px 0;
            padding: 8px;
            background: #f5f5f5;
            border-left: 3px solid #333;
            font-size: 11px;
          }
          .totals { 
            margin: 0; 
            padding: 12px 0 0 0;
          }
          .totals .row {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .footer {
            text-align: center;
            margin-top: 0;
            padding-top: 12px;
            font-size: 12px;
            font-style: italic;
          }
          hr {
            border: none;
            border-top: 2px solid #000;
            margin: 12px 0;
          }
          hr.thin {
            border-top: 1px solid #999;
            margin: 6px 0;
          }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <h2>${
          escapeHtml(req.restaurant?.name) ||
          escapeHtml(hotelInfo?.name) ||
          "Hotel Restaurant"
        }</h2>
        <h3>Dining Order Receipt</h3>
        
        <div class="info-section">
          <div class="row"><strong>Order ID:</strong></div>
          <div class="row" style="font-size:10px;word-break:break-all;margin-top:-2px;margin-bottom:6px;">
            ${escapeHtml(req.requestId)}
          </div>
          <div class="row"><strong>Date:</strong><span>${formatDate(
            req.createdAt,
          )}</span></div>
          <div class="row"><strong>Guest:</strong><span>${escapeHtml(
            req.guestInfo?.name || req.guestName || "N/A",
          )}</span></div>
          <div class="row"><strong>Room:</strong><span>${
            escapeHtml(req.room?.roomNumber) || "N/A"
          }</span></div>
          ${
            req.room?.type
              ? `<div class="row"><strong>Room Type:</strong><span>${escapeHtml(
                  req.room.type,
                )}</span></div>`
              : ""
          }
        </div>
        ${
          req.dietaryRestrictions && req.dietaryRestrictions.length > 0
            ? `<div class="delivery-section" style="background:#fff3cd;border-left:3px solid #856404;">
                 <strong>Dietary Restrictions:</strong><br/>
                 ${req.dietaryRestrictions
                   .map((r) => escapeHtml(r.name || r.code || "Unknown"))
                   .join(", ")}
               </div>`
            : ""
        }
        <hr/>

        ${
          req.deliveryInstructions
            ? `<div class="delivery-section">
                 <strong>Delivery Instructions:</strong><br/>
                 ${escapeHtml(req.deliveryInstructions)}
               </div>
               <hr/>`
            : ""
        }

        <div class="items-section">
          <div class="items-header">Order Items</div>
          <hr class="thin"/>
          ${
            itemsHtml ||
            '<div style="text-align:center;color:#999;">No items</div>'
          }
        </div>

        <hr/>
        <div class="totals">
          <div class="row"><span>Subtotal:</span><span>$${
            req.totalAmount !== null && req.totalAmount !== undefined
              ? Number(req.totalAmount).toFixed(2)
              : (() => {
                  const sum = (req.orderItems || []).reduce((acc, it) => {
                    const unit = parseFloat(it.price?.amount ?? "0");
                    return acc + unit * (it.quantity || 1);
                  }, 0);
                  return sum.toFixed(2);
                })()
          }</span></div>
          <hr class="thin"/>
          <div class="row" style="font-size:16px;margin-top:8px;"><span>TOTAL:</span><span>$${
            req.totalAmount !== null && req.totalAmount !== undefined
              ? Number(req.totalAmount).toFixed(2)
              : (() => {
                  const sum = (req.orderItems || []).reduce((acc, it) => {
                    const unit = parseFloat(it.price?.amount ?? "0");
                    return acc + unit * (it.quantity || 1);
                  }, 0);
                  return sum.toFixed(2);
                })()
          }</span></div>
          <hr class="thin" style="margin:8px 0;"/>
          <div class="row" style="font-size:12px;font-weight:normal;">
            <span>Payment Method:</span>
            <span>${escapeHtml(
              req.paymentMethod?.replace(/_/g, " ") ||
                req.paymentStatus ||
                "N/A",
            )}</span>
          </div>
        </div>
      </body>
    </html>`;
  };

  const handlePrintReceipt = () => {
    if (!selectedRequest) {
      console.error("No request selected for printing");
      return;
    }

    console.log("Printing receipt for request:", selectedRequest.requestId);

    // Use about:blank explicitly and _blank target to fix Arc browser issues
    const printWindow = window.open("about:blank", "_blank");

    console.log("Window opened:", printWindow !== null);

    if (!printWindow) {
      console.error("Failed to open print window");
      alert(
        "Unable to open print window. Please check your popup blocker settings.",
      );
      return;
    }

    try {
      const rawHtml = generateReceiptHTML(selectedRequest);
      console.log("Generated HTML length:", rawHtml.length);

      // Sanitize the generated HTML before writing it into the new window
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        WHOLE_DOCUMENT: true,
        RETURN_DOM: false,
        RETURN_DOM_FRAGMENT: false,
        ADD_TAGS: ["style", "meta"],
        ADD_ATTR: ["style", "class", "charset"],
        KEEP_CONTENT: true,
      });

      console.log("Sanitized HTML length:", sanitizedHtml.length);

      printWindow.document.open();
      printWindow.document.write(sanitizedHtml);
      printWindow.document.close();

      console.log("Document written, waiting to render...");

      // Give the browser time to render content before printing
      setTimeout(() => {
        console.log("Triggering print dialog");
        printWindow.focus();
        printWindow.print();
        // Don't auto-close so user can see the receipt
        // printWindow.close();
      }, 500);
    } catch (err) {
      console.error("Error generating receipt:", err);
      // If anything goes wrong, close the popup to avoid leaving a blank tab
      try {
        printWindow.close();
      } catch (closeErr) {
        console.error("Error closing window:", closeErr);
      }
      alert("Error generating receipt. Please try again.");
    }
  };

  if (isLoading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <PageErrorView title="Error loading dining requests" onRetry={refetch} />
    );
  }

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header + Subheader */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
          Dining Requests
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Keep track of all your guest's dining requests on this dashboard.
        </p>
      </div>

      {/* Error Alert */}
      {(error || statusError || messageTypeError) && (
        <div className="mb-4">
          <Alert
            variant="error"
            title="Error"
            message={
              statusError ||
              messageTypeError?.message ||
              getErrorMessage(error) ||
              "An unexpected error occurred"
            }
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <RequestsTab
          onFrameChange={setSelectedFrame}
          selectedFrame={selectedFrame}
          options={["Active", "Scheduled", "History"]}
        />
      </div>

      {/* Refresh + Search + Filter + Sort + Favorites */}
      <RequestsToolbar
        searchQuery={searchTermLocal}
        onSearchChange={(value) => {
          setSearchTermLocal(value);
          if (value === "") {
            setSearchTerm("");
          }
        }}
        onSearchSubmit={() => {
          setSearchTerm(searchTermLocal);
          setPage(1); // Reset to first page when searching
        }}
        isLoading={isLoading}
        onRefresh={() => window.location.reload()}
      >
        <>
          {selectedFrame !== "Scheduled" && (
            <>
              {selectedStatus !== "All" && (
                <div className="bg-white border border-gray-100 rounded-lg dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]">
                  <button
                    className="text-black"
                    onClick={() => {
                      setSelectedStatus("All");
                    }}
                  >
                    <div className="flex w-full h-full mx-2 items-center gap-2 font-light text-sm px-2 py-2">
                      {selectedStatus}
                      <CloseIcon />
                    </div>
                  </button>
                </div>
              )}
              <div className="relative inline-block w-full md:w-56">
                <button
                  className="flex dropdown-toggle w-full items-center justify-between 
                  px-4 py-2 text-sm text-black bg-white border rounded-md
                  hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
                  onClick={toggleFilterDropdown}
                >
                  <span className="px-4">Filter</span>
                  <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
                </button>
                <Dropdown
                  isOpen={isFilterDropdownOpen}
                  onClose={toggleFilterDropdown}
                  className="w-full absolute z-100"
                >
                  {getFilterOptions(selectedFrame).map((option) => (
                    <DropdownItem
                      className="dark:text-white/90"
                      key={option}
                      onItemClick={() => {
                        setSelectedStatus(option);
                        toggleFilterDropdown();
                      }}
                    >
                      {option}
                    </DropdownItem>
                  ))}
                </Dropdown>
              </div>
            </>
          )}
          <div className="relative inline-block w-full md:w-56">
            <button
              className="flex dropdown-toggle w-full items-center justify-between 
              px-4 py-2 text-sm text-black bg-white border rounded-md
              hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
              onClick={toggleSortDropdown}
            >
              <span className="px-4">{`Sort By ${selectedSort}`}</span>
              <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
            </button>
            <Dropdown
              isOpen={isSortDropdownOpen}
              onClose={toggleSortDropdown}
              className="w-full absolute z-100"
            >
              {["Time", "Request ID"].map((option) => (
                <DropdownItem
                  className="dark:text-white/90"
                  key={option}
                  onItemClick={() => {
                    setSelectedSort(option);
                    setPage(1);
                    toggleSortDropdown();
                  }}
                >
                  {`Sort By ${option}`}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <button
            className={`flex items-center justify-center px-4 py-2 text-sm border rounded-md
              ${
                showStarredOnly
                  ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800"
                  : "bg-white text-black border-gray-200 dark:bg-white/[0.03] dark:text-white/90 dark:border-gray-800"
              }`}
            onClick={() => {
              setShowStarredOnly(!showStarredOnly);
              setPage(1);
            }}
          >
            {showStarredOnly ? (
              <StarIconSolid className="w-5 h-5" />
            ) : (
              <StarIcon className="w-5 h-5" />
            )}
          </button>
        </>
      </RequestsToolbar>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!fetchedRequests || paginatedData.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500 dark:text-gray-400">
              No {selectedFrame.toLowerCase()} requests available
            </p>
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-800">
              <TableRow>
                <TableCell className="font-light dark:text-gray-400 w-12">
                  <span></span>
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-40"
                >
                  Guest
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-32"
                >
                  Room Number
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-40"
                >
                  Status
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-1 pr-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-40"
                >
                  Time Submitted
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-40"
                >
                  Total Amount
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y-[1px] divide-gray-200 dark:divide-gray-800">
              {paginatedData.map((request) => (
                <TableRow
                  key={request.requestId}
                  className={`${getRowClassName(
                    request.status,
                  )} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                    selectedRequest?.requestId === request.requestId
                      ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500"
                      : ""
                  }`}
                  onClick={() => handleSelectRequest(request)}
                >
                  <TableCell className="py-3 w-12">
                    <button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        toggleStars(request.requestId);
                      }}
                    >
                      {starredList.has(request.requestId) ? (
                        <StarIconSolid className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-40 ${getFrameClass()}`}
                  >
                    <div className="truncate max-w-[160px]">
                      {displayValue(request.guestName)}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-32 ${getFrameClass()}`}
                  >
                    {displayValue(request.room?.roomNumber)}
                  </TableCell>
                  <TableCell className={`py-3 pr-1 w-40 min-w-[120px] ${getFrameClass()}`}>
                    <div className="relative inline-block">
                      <button
                        ref={(el) => {
                          statusButtonRefs.current[request.requestId] = el;
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStatusDropdown(request.requestId);
                        }}
                        disabled={updatingStatus[request.requestId]}
                        className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingStatus[request.requestId] ? (
                          <div className="flex items-center gap-2">
                            <LoadingSpinner size="sm" color="primary" />
                            <span>Updating...</span>
                          </div>
                        ) : (
                          <>
                            <span className="mr-2">
                              {getStatusDisplay(request.status) ===
                              "In Progress" ? (
                                <>
                                  <span className="hidden xsm:inline whitespace-nowrap">
                                    In Progress
                                  </span>
                                  <span className="inline xsm:hidden whitespace-nowrap">
                                    In Prog
                                  </span>
                                </>
                              ) : (
                                <span className="whitespace-nowrap">
                                  {getStatusDisplay(request.status)}
                                </span>
                              )}
                            </span>
                            <ChevronDownIcon className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      {statusDropdowns[request.requestId] &&
                        !updatingStatus[request.requestId] && (
                          <Dropdown
                            isOpen={statusDropdowns[request.requestId]}
                            onClose={() =>
                              setStatusDropdowns((prev) => ({
                                ...prev,
                                [request.requestId]: false,
                              }))
                            }
                            className="w-36 z-50"
                            usePortal={true}
                            buttonRef={
                              {
                                current:
                                  statusButtonRefs.current[request.requestId],
                              } as React.RefObject<HTMLElement>
                            }
                          >
                            {getStatusOptions()
                              .filter(
                                (opt) =>
                                  opt !== getStatusDisplay(request.status),
                              )
                              .map((opt) => (
                                <DropdownItem
                                  key={opt}
                                  onItemClick={() => {
                                    handleTableStatusChange(
                                      request.requestId,
                                      opt,
                                    );
                                  }}
                                  className="dark:text-white"
                                >
                                  {opt}
                                </DropdownItem>
                              ))}
                          </Dropdown>
                        )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-1 pr-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-32 ${getFrameClass()}`}
                  >
                    <SimpleDate date={request.createdAt} />
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-40 ${getFrameClass()}`}
                  >
                    {request.totalAmount !== null &&
                    request.totalAmount !== undefined
                      ? `$${Number(request.totalAmount).toFixed(2)}`
                      : displayValue(request.totalAmount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
          <select
            value={pagination.limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-8"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            items per page
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Previous
          </button>
          <span className="text-sm dark:text-gray-400">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(pagination.page + 1)}
            disabled={!hasMore}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Next
          </button>
        </div>
      </div>

      {/* Backdrop: click outside sidebar (including header/app nav) to close all sidebars */}
      {(selectedRequest !== null || showMessageHistory) && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 top-0 left-0 right-0 bottom-0"
          onClick={() => {
            setShowMessageHistory(false);
            setSelectedRequest(null);
          }}
        />
      )}
      {/* SIDEBAR CONTAINER WITH ANIMATION */}
      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] z-50
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto 
          transition-transform duration-300 ease-in-out transform
          ${showMessageHistory ? "hidden" : ""}
          ${selectedRequest !== null ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {selectedRequest !== null && (
          <>
            {/* Navigation and Close Button */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the first item of the page and not on page 1, go to previous page
                    if (currentIndex === 0 && pagination.page > 1) {
                      setPendingNavigation("previous");
                      setPage(pagination.page - 1);
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex > 0) {
                      handleSelectRequest(sortedData[currentIndex - 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) === 0 &&
                      pagination.page === 1)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous request"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the last item of the page and there's a next page, go to next page
                    if (currentIndex === sortedData.length - 1 && hasMore) {
                      setPendingNavigation("next");
                      setPage(pagination.page + 1);
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex < sortedData.length - 1) {
                      handleSelectRequest(sortedData[currentIndex + 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) ===
                      sortedData.length - 1 &&
                      !hasMore)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next request"
                >
                  <ChevronLeftIcon className="w-5 h-5 rotate-180" />
                </button>
              </div>
              <button
                className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
                onClick={() => setSelectedRequest(null)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex items-center gap-5 mb-4">
              <button onClick={() => toggleStars(selectedRequest.requestId)}>
                {starredList.has(selectedRequest.requestId) ? (
                  <StarIconSolid className="w-5 h-5 text-yellow-500" />
                ) : (
                  <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              <button
                onClick={handlePrintReceipt}
                className="text-gray-700 hover:text-black dark:text-gray-300 dark:hover:text-white"
                title="Print Receipt"
              >
                <PrinterIcon className="w-5 h-5" />
              </button>
              <p className="font-light text-gray-500 dark:text-gray-300 italic">
                Submitted:{" "}
                {formatDateWithTimezone(
                  selectedRequest.createdAt,
                  hotelInfo?.timezone,
                )}
              </p>
            </div>
            <div className="flex items-center mb-4">
              <p className="font-medium text-black dark:text-white mr-2">
                Status:
              </p>
              <div className="relative">
                <button
                  onClick={() =>
                    setIsSidebarStatusDropdownOpen(!isSidebarStatusDropdownOpen)
                  }
                  className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-gray-50 dark:bg-gray-700 text-black dark:text-white"
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <>
                      <span className="mr-1">
                        {getStatusDisplay(selectedRequest.status)}
                      </span>
                      <ChevronDownIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
                {isSidebarStatusDropdownOpen && (
                  <Dropdown
                    isOpen={isSidebarStatusDropdownOpen}
                    onClose={() => setIsSidebarStatusDropdownOpen(false)}
                    className="w-36"
                  >
                    {getStatusOptions().map((opt) => (
                      <DropdownItem
                        key={opt}
                        onItemClick={() =>
                          handleSidebarStatusChange(
                            opt
                              .toUpperCase()
                              .replace(/\s/g, "_") as RequestStatus,
                          )
                        }
                        className="dark:text-white"
                      >
                        {opt}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Note: The guest will be notified of this status change.
            </p>

            {/* Room Details and Delivery Instructions */}
            <div className="mb-4 space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Room Details
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Name:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.guestInfo?.name || "N/A"}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Room Number:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.room?.roomNumber || "N/A"}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Room Type:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.room?.type || "N/A"}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Floor:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.room?.floor || "N/A"}
                </p>
              </div>
              <div>
                <p className="font-medium text-black dark:text-white mb-1">
                  Delivery Instructions:
                </p>
                <p className="font-light text-black dark:text-white italic">
                  {displayValue(
                    selectedRequest.deliveryInstructions,
                    "No delivery instructions provided.",
                  )}
                </p>
              </div>
            </div>

            {/* Dietary Restrictions */}
            {selectedRequest.dietaryRestrictions &&
              selectedRequest.dietaryRestrictions.length > 0 && (
                <div className="mb-4 text-sm">
                  <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                    Dietary Restrictions
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedRequest.dietaryRestrictions.map(
                      (restriction, index) => (
                        <span
                          key={restriction.code || index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                        >
                          {restriction.name || restriction.code || "Unknown"}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

            {/* Order Information */}
            <div className="mb-4 space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Order Information
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Payment Method:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.paymentMethod
                    ?.replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </p>
              </div>
              {selectedRequest.orderItems &&
                selectedRequest.orderItems.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="mb-4">
                      {(() => {
                        const orderItems = selectedRequest.orderItems!;
                        return (
                          <Table className="w-full">
                            <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-800">
                              <TableRow>
                                <TableCell className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400">
                                  Item
                                </TableCell>
                                <TableCell className="py-3 font-light text-[0.875rem] text-black text-center dark:text-gray-400 w-24">
                                  Quantity
                                </TableCell>
                                <TableCell className="py-3 font-light text-[0.875rem] text-black text-end dark:text-gray-400 w-24">
                                  Price
                                </TableCell>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {orderItems.map((item, index) => (
                                <React.Fragment key={item.orderItemId}>
                                  <TableRow
                                    className={`${
                                      index !== orderItems.length - 1
                                        ? "border-b border-gray-200 dark:border-gray-800"
                                        : ""
                                    } hover:bg-gray-100 dark:hover:bg-gray-800/60`}
                                  >
                                    <TableCell className="py-1.5 text-[0.8125rem] font-light text-black dark:text-white w-[400px]">
                                      <div className="whitespace-normal break-words">
                                        {displayValue(
                                          item.itemName,
                                          "Unknown item",
                                        )}
                                      </div>
                                      {item.specialInstructions &&
                                        String(
                                          item.specialInstructions,
                                        ).trim() !== "" && (
                                          <div className="mt-1 text-[0.75rem] text-gray-600 dark:text-gray-400 italic border-l-2 border-gray-300 dark:border-gray-700 pl-3 py-0.5 whitespace-pre-wrap">
                                            <span className="font-medium">
                                              Note:
                                            </span>{" "}
                                            {displayValue(
                                              item.specialInstructions,
                                              "No special instructions.",
                                            )}
                                          </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-[0.8125rem] font-light text-black dark:text-white text-center">
                                      {displayValue(item.quantity)}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-[0.8125rem] font-light text-black dark:text-white text-end">
                                      {isLoadingPrices ? (
                                        <LoadingSpinner size="sm" />
                                      ) : (
                                        (() => {
                                          const fetched =
                                            menuItemPrices[item.priceId];
                                          const raw = parseFloat(
                                            item.price?.amount ?? "0",
                                          );
                                          const value =
                                            fetched && fetched > 0
                                              ? fetched
                                              : raw;
                                          return `$${value.toFixed(2)}`;
                                        })()
                                      )}
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))}
                              <TableRow className="border-t border-gray-200 dark:border-gray-600">
                                <TableCell className="py-3 text-[0.8125rem] font-medium text-black dark:text-white">
                                  Total Amount:
                                </TableCell>
                                <TableCell className="py-3 text-[0.8125rem] text-center">
                                  <span></span>
                                </TableCell>
                                <TableCell className="py-3 text-[0.8125rem] font-light text-black dark:text-white text-end">
                                  {selectedRequest.totalAmount !== null &&
                                  selectedRequest.totalAmount !== undefined
                                    ? `$${Number(
                                        selectedRequest.totalAmount,
                                      ).toFixed(2)}`
                                    : displayValue(selectedRequest.totalAmount)}
                                </TableCell>
                                <TableCell className="py-3 text-center">
                                  <span></span>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        );
                      })()}
                    </div>
                  </div>
                )}
              {selectedRequest.status === "SCHEDULED" && (
                <div className="flex items-start">
                  <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                    Scheduled Time:
                  </p>
                  <p className="font-light text-black dark:text-white break-words">
                    {selectedRequest.scheduledTime
                      ? formatDateWithTimezone(
                          selectedRequest.scheduledTime,
                          hotelInfo?.timezone,
                        )
                      : displayValue(selectedRequest.scheduledTime)}
                  </p>
                </div>
              )}
              {selectedRequest.status === "COMPLETED" &&
                selectedRequest.completedAt && (
                  <div className="flex items-start">
                    <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                      Completed At:
                    </p>
                    <p className="font-light text-black dark:text-white break-words">
                      {formatDateWithTimezone(
                        selectedRequest.completedAt,
                        hotelInfo?.timezone,
                      )}
                    </p>
                  </div>
                )}
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                Send a Message
              </h3>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-600 border-b border-gray-300 dark:border-gray-600 rounded-t-lg border-x-0 border-t-0"
                  placeholder="Enter subject (optional)..."
                />
                <div className="px-3 py-2 border-b border-gray-300 dark:border-gray-600">
                  <label
                    htmlFor="messageTypeDining"
                    className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Message Type
                  </label>
                  {isLoadingMessageTypes ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading types...
                    </p>
                  ) : messageTypeError ? (
                    <p className="text-sm text-red-500">
                      Error: {messageTypeError.message}
                    </p>
                  ) : (
                    <select
                      id="messageTypeDining"
                      value={selectedMessageTypeId}
                      onChange={(e) => setSelectedMessageTypeId(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none text-sm rounded-md p-1 border border-gray-300 dark:border-gray-600"
                      disabled={messageTypes.length === 0}
                    >
                      {messageTypes.length === 0 && (
                        <option key="no-types" value="">
                          No types available
                        </option>
                      )}
                      {messageTypes.map((type) => (
                        <option key={type.typeid} value={type.typeid}>
                          {type.type}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <textarea
                  value={liveStatus}
                  onChange={(e) => setLiveStatus(e.target.value)}
                  rows={3}
                  className="w-full px-3 pt-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 border-0 rounded-b-lg"
                  placeholder="Enter message content (required)..."
                  disabled={isPushingStatus}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => setShowMessageHistory(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600"
                >
                  View Message History
                </button>
                <button
                  onClick={handlePushToGuest}
                  disabled={isPushingStatus}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#1100D8] rounded-md hover:bg-[#0e00b8] dark:bg-white/10 dark:hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPushingStatus ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    "Send to Guest"
                  )}
                </button>
              </div>
              {showPushAlert && (
                <div className="mt-3">
                  <Alert
                    variant="success"
                    title=""
                    message="Message successfully sent to guest!"
                  />
                </div>
              )}
            </div>
            <div className="space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Request Details
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Request ID:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.requestId)}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Email:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.guestInfo?.email || "N/A"}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Phone:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.guestInfo?.phone || "N/A"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
        title="Confirm Message"
        message={`Are you sure you want to send this message to the guest?${
          pendingMessage?.subject
            ? `\n\nSubject: ${pendingMessage.subject}`
            : ""
        }\n\nContent: ${pendingMessage?.content || ""}`}
        isLoading={isPushingStatus}
      />

      {/* Message History Sidebar */}
      <MessageHistorySidebar
        isOpen={showMessageHistory}
        onClose={() => {
          setShowMessageHistory(false);
          try {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("requestId");
              return next;
            });
          } catch (_e) {
            if (import.meta.env.DEV)
              console.warn("Failed to clear requestId param", _e);
          }
        }}
        requestId={selectedRequest?.requestId}
      />

      {/* Status Update Success Toast */}
      {showStatusUpdateAlert && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Alert
            variant="success"
            title="Status Updated"
            message="Request status has been updated successfully."
          />
        </div>
      )}
    </div>
  );
}
